import type { AxiosInstance, RawAxiosResponseHeaders } from 'axios';
import type { PlatformRequestConfig, PlatformResponse } from '@cloudquery/api-platform-multi-tenant';

const toRequestHeaders = (headers: PlatformRequestConfig['headers']): Record<string, string> =>
  Object.fromEntries(new Headers(headers ?? {}).entries());

const toResponseHeaders = (headers: RawAxiosResponseHeaders): Headers =>
  new Headers(Object.entries(headers).filter(([, value]) => typeof value === 'string') as [string, string][]);

/* The generated client is written against fetch, so it expects a fetch-shaped response. env0 keeps axios
   for its token refresh, retry and pay-gate interceptors, and synthesises the parts of that shape it
   does not read.
   `hideNotification` and `hidePayGateModal` opt out of env0's global error notification and its
   "Feature Unavailable" modal: CloudQuery data is supplementary - an org without a CloudQuery connection
   should not be nagged - so the insights UI renders its own empty and error states instead. The proxy is
   pay gated, so without the opt-out a 402 opens a blocking modal over whatever page the reader is on by
   the time the request settles, which is not necessarily the one that asked for insights. */
export const cloudQueryPlatformFetcher =
  ({ axiosInstance, getOrganizationId }: { axiosInstance: AxiosInstance; getOrganizationId: () => string | null }) =>
  async <T>(url: string, config: PlatformRequestConfig): Promise<PlatformResponse<T>> => {
    const organizationId = getOrganizationId();

    if (!organizationId) {
      throw new Error('Cannot call the CloudQuery API before an organization is selected');
    }

    const { data, status, statusText, headers } = await axiosInstance.request<T>({
      hideNotification: true,
      hidePayGateModal: true,
      url: `/cloudquery/proxy${url}`,
      method: config.method,
      data: config.body,
      headers: toRequestHeaders(config.headers),
      params: { ...config.params, organizationId },
      paramsSerializer: { indexes: null },
      signal: config.signal ?? undefined
    });

    return {
      data,
      status,
      statusText,
      headers: toResponseHeaders(headers),
      response: new Response(null, { status, statusText })
    };
  };
